import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { campaignApi } from '@/api/feedback/campaignApi';
import { type Location } from '@/models/account-management/Location';
import { CampaignScheduleScope, type Campaign, type CampaignSchedule } from '@/models/feedback/Campaign';
import { useLocalizedStrings } from '../../../localization/LocalizedStringsProvider';
import { useToast } from '../../common/toast/ToastProvider';
import ReadOnlyFieldset from '../../common/dialog/ReadOnlyFieldset';

const pad = (n: number) => String(n).padStart(2, '0');
const toLocalInput = (iso?: string) => {
    if (!iso) return '';
    const d = new Date(iso);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fromLocalInput = (value: string) => new Date(value).toISOString();

interface Props {
    open: boolean;
    campaignId?: string;        // fixed campaign (from the campaign editor); omit to let the user pick one
    campaigns?: Campaign[];     // choices for the picker when campaignId is omitted
    brandId: string;
    locations: Location[];
    schedule: CampaignSchedule | null; // null = create
    // View-only: the grid opens this with an eye rather than a pencil for read access alone.
    readOnly?: boolean;
    onClose: () => void;
}

const ScheduleDialog = ({ open, campaignId, campaigns, brandId, locations, schedule, readOnly, onClose }: Props) => {
    const qc = useQueryClient();
    const toast = useToast();
    const strings = useLocalizedStrings();

    const [selectedCampaignId, setSelectedCampaignId] = useState<string>('');
    const [scope, setScope] = useState<CampaignScheduleScope>(CampaignScheduleScope.Brand);
    const [locationId, setLocationId] = useState<string>('');
    const [locationGroupId, setLocationGroupId] = useState<string>('');
    const [start, setStart] = useState<string>('');
    const [end, setEnd] = useState<string>('');

    useEffect(() => {
        if (!open) return;
        setSelectedCampaignId(campaignId ?? '');
        setScope(schedule?.scope ?? CampaignScheduleScope.Brand);
        setLocationId(schedule?.locationId ?? '');
        setLocationGroupId(schedule?.locationGroupId ?? '');
        setStart(toLocalInput(schedule?.startDateTime));
        setEnd(toLocalInput(schedule?.endDateTime));
    }, [open, schedule, campaignId]);

    // The campaign this schedule belongs to: the fixed one, or the picked one on the schedule page.
    const effectiveCampaignId = campaignId ?? selectedCampaignId;

    const { mutateAsync: save, isPending } = useMutation({
        mutationFn: async () => {
            const body = {
                scope,
                brandId: scope === CampaignScheduleScope.Brand ? brandId : null,
                locationId: scope === CampaignScheduleScope.Location ? locationId : null,
                locationGroupId: scope === CampaignScheduleScope.LocationGroup ? locationGroupId : null,
                startDateTime: fromLocalInput(start),
                endDateTime: fromLocalInput(end),
            };
            if (schedule) {
                await campaignApi.updateSchedule(schedule.id, body);
            } else {
                await campaignApi.createSchedule(effectiveCampaignId, body);
            }
        },
        onSuccess: async () => {
            toast({ severity: 'success', message: strings.feedbackScheduleSaved });
            await qc.invalidateQueries({ queryKey: ['feedback-schedules', effectiveCampaignId] });
            onClose();
        },
        onError: () => toast({ severity: 'error', message: strings.feedbackScheduleSaveError }),
    });

    const targetValid =
        scope === CampaignScheduleScope.Brand ||
        (scope === CampaignScheduleScope.Location && !!locationId) ||
        (scope === CampaignScheduleScope.LocationGroup && !!locationGroupId);
    const canSave = !!effectiveCampaignId && !!start && !!end && targetValid;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{schedule ? strings.feedbackEditSchedule : strings.feedbackNewSchedule}</DialogTitle>
            <DialogContent>
                <ReadOnlyFieldset readOnly={readOnly}>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    {campaignId ? (
                        <Typography variant="body1">
                            <strong>{strings.feedbackCampaign}:</strong> {campaigns?.find((c) => c.id === campaignId)?.name ?? ''}
                        </Typography>
                    ) : (
                        <FormControl fullWidth>
                            <InputLabel id="schedule-campaign-label">{strings.feedbackCampaign}</InputLabel>
                            <Select
                                labelId="schedule-campaign-label"
                                label={strings.feedbackCampaign}
                                value={selectedCampaignId}
                                onChange={(e) => setSelectedCampaignId(e.target.value as string)}
                            >
                                {(campaigns ?? []).map((c) => (
                                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    <FormControl fullWidth>
                        <InputLabel id="schedule-scope-label">{strings.feedbackScope}</InputLabel>
                        <Select
                            labelId="schedule-scope-label"
                            label={strings.feedbackScope}
                            value={scope}
                            onChange={(e) => setScope(Number(e.target.value) as CampaignScheduleScope)}
                        >
                            <MenuItem value={CampaignScheduleScope.Brand}>{strings.feedbackScopeBrand}</MenuItem>
                            <MenuItem value={CampaignScheduleScope.Location}>{strings.feedbackScopeLocation}</MenuItem>
                            {/* Location Group scope TEMPORARILY DISABLED while the legacy Feedback tempdb is the
                                source of truth for campaign schedules (group membership sync is too fragile).
                                Re-enable when the Feedback service is the single source of truth (tempdb gone). */}
                            {/* <MenuItem value={CampaignScheduleScope.LocationGroup}>{strings.feedbackScopeLocationGroup}</MenuItem> */}
                        </Select>
                    </FormControl>

                    {scope === CampaignScheduleScope.Location && (
                        <FormControl fullWidth>
                            <InputLabel id="schedule-location-label">{strings.feedbackLocation}</InputLabel>
                            <Select
                                labelId="schedule-location-label"
                                label={strings.feedbackLocation}
                                value={locationId}
                                onChange={(e) => setLocationId(e.target.value as string)}
                            >
                                {locations.map((l) => (
                                    <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    {scope === CampaignScheduleScope.LocationGroup && (
                        <TextField
                            label={strings.feedbackLocationGroupId}
                            value={locationGroupId}
                            onChange={(e) => setLocationGroupId(e.target.value)}
                            helperText={strings.feedbackLocationGroupHelp}
                            fullWidth
                        />
                    )}

                    <TextField
                        label={strings.feedbackStart}
                        type="datetime-local"
                        value={start}
                        onChange={(e) => setStart(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                    />
                    <TextField
                        label={strings.feedbackEnd}
                        type="datetime-local"
                        value={end}
                        onChange={(e) => setEnd(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                    />
                </Stack>
            </ReadOnlyFieldset>
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={onClose}>{readOnly ? strings.close : strings.cancel}</Button>
                {!readOnly && <Button variant="contained" loading={isPending} disabled={!canSave || isPending} onClick={() => save()}>{strings.save}</Button>}
            </DialogActions>
        </Dialog>
    );
};

export default ScheduleDialog;
